import { CommandGroup, CommandItem } from "@/components/ui/command";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils";
import type { IntegrationConnection } from "@cohesive/api-client";
import { IntegrationIcon } from "@cohesive/ui-components";
import { Blocks } from "lucide-react";

/** Reads as a list while loading without pushing the menu past where it lands. */
const LOADING_ROW_WIDTHS = ["w-24", "w-32", "w-20"];

interface ConnectorsMenuSectionProps {
  /** Connectors matching the current `@` query — what the list renders. */
  connections: IntegrationConnection[];
  /**
   * Whether any mentionable connector exists at all, ignoring the query. Keeps
   * "nothing connected" (teach the path to connector settings) distinct from
   * "nothing matches what you typed" (the list is fine, the query isn't).
   */
  hasConnections: boolean;
  isLoading: boolean;
  /**
   * The roster failed to load. React Query keeps serving cached `data` through
   * a failed background refetch, so this only reaches the menu when there is
   * nothing cached — a transient failure must not hide usable connectors.
   */
  isError: boolean;
  onSelectConnection: (connection: IntegrationConnection) => void;
  /** Opens connector settings; only reachable while nothing is connected. */
  onBrowseConnectors: () => void;
}

const MessageRow = ({ children }: { children: React.ReactNode }) => (
  <p className="px-2 py-1.5 text-sm text-muted-foreground">{children}</p>
);

/**
 * The connectors section of the composer's `@` menu.
 *
 * A cold load used to be indistinguishable from an empty account — both
 * rendered nothing — so users with zero connectors never learned connectors
 * existed. Loading reserves rows; the zero state names the payoff and offers
 * connector settings.
 */
export const ConnectorsMenuSection: React.FC<ConnectorsMenuSectionProps> = ({
  connections,
  hasConnections,
  isLoading,
  isError,
  onSelectConnection,
  onBrowseConnectors,
}) => {
  if (isLoading) {
    return (
      <CommandGroup className="p-0">
        <div role="status" aria-label="Loading connectors">
          {LOADING_ROW_WIDTHS.map((width) => (
            <div key={width} className="flex items-center gap-2 px-2 py-1.5">
              <Skeleton className="size-4 shrink-0 rounded" />
              <Skeleton className={cn("h-3.5", width)} />
            </div>
          ))}
        </div>
      </CommandGroup>
    );
  }

  if (!hasConnections) {
    if (isError) {
      return (
        <CommandGroup className="p-0">
          <MessageRow>Couldn&apos;t load connectors.</MessageRow>
        </CommandGroup>
      );
    }

    return (
      <CommandGroup className="p-0">
        <div className="px-2 pb-1 pt-1.5">
          <p className="text-sm text-foreground">
            No connectors to mention yet.
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Connect Gmail, Slack, or Drive to pull their records into chat.
          </p>
        </div>
        <CommandItem value="browse-connectors" onSelect={onBrowseConnectors}>
          <Blocks />
          Browse connectors
        </CommandItem>
      </CommandGroup>
    );
  }

  if (connections.length === 0) {
    return (
      <CommandGroup className="p-0">
        <MessageRow>No matching connectors.</MessageRow>
      </CommandGroup>
    );
  }

  return (
    <CommandGroup className="p-0">
      {connections.map((connection) => (
        <CommandItem
          key={connection.id}
          onSelect={() => onSelectConnection(connection)}
        >
          <IntegrationIcon
            platform={connection.integration_platform}
            name={connection.integration_name}
            imageURL={connection.integration_image_url}
            className="size-4 rounded object-cover"
          />
          <span>{connection.integration_name}</span>
        </CommandItem>
      ))}
    </CommandGroup>
  );
};
